/**
 * useAuth.tsx
 * 
 * Hook principale per la gestione dell'autenticazione e dei ruoli utente.
 * Fornisce un contesto React con lo stato di autenticazione e funzioni per:
 * - Login, registrazione e logout
 * - Gestione gerarchica dei ruoli (dal più alto al più basso)
 * - Simulazione ruoli per testing (solo ruoli inferiori al proprio)
 * 
 * La gerarchia dei ruoli determina i permessi: un utente può fare
 * tutto ciò che possono fare i ruoli inferiori al proprio.
 * 
 * @example
 * const { user, hasRoleLevel, signIn, signOut } = useAuth();
 * if (hasRoleLevel('autore')) { // può modificare contenuti }
 */

import { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * Gerarchia dei ruoli ordinati dal più alto (più privilegi) al più basso.
 * L'indice nell'array determina il livello: 0 = massimo, 9 = minimo.
 * Usato per confronti gerarchici: un ruolo può fare tutto ciò che
 * possono fare i ruoli con indice maggiore del proprio.
 */
export const ROLE_HIERARCHY = [
  "amministratore",       // 0 - Controllo completo del sistema
  "responsabile",         // 1 - Gestione utenti e contenuti
  "segreteria",           // 2 - Gestione anagrafiche e iscrizioni
  "coordinatore_cft",     // 3 - Coordinatore CFT (sola lettura, stesso livello formatore)
  "autore",               // 3 - Creazione contenuti
  "formatore",            // 4 - Creazione corsi e valutazione
  "insegnante_cft",       // 5 - Insegnante CFT (inferiore a formatore)
  "tutor",                // 6 - Supporto didattico
  "istruttore",           // 7 - Insegnamento e valutazione allievi
  "aspirante_istruttore", // 8 - In formazione come istruttore
  "aiuto_istruttore",     // 9 - Supporto all'istruttore
  "allievo",              // 10 - Partecipa ai corsi
  "socio",                // 11 - Socio LNI base
  "utente",               // 12 - Utente generico non socio
] as const;

/** Tipo union dei ruoli possibili, derivato dalla gerarchia */
export type AppRole = typeof ROLE_HIERARCHY[number];

/** Etichette leggibili per i ruoli da mostrare nell'UI */
export const ROLE_LABELS: Record<AppRole, string> = {
  amministratore: "Amministratore",
  responsabile: "Responsabile",
  segreteria: "Segreteria",
  coordinatore_cft: "Coordinatore CFT",
  autore: "Autore",
  formatore: "Formatore",
  insegnante_cft: "Insegnante CFT",
  tutor: "Tutor",
  istruttore: "Istruttore",
  aspirante_istruttore: "Aspirante Istruttore",
  aiuto_istruttore: "Aiuto Istruttore",
  allievo: "Allievo",
  socio: "Socio",
  utente: "Utente",
};

interface SignUpData {
  firstName: string;
  lastName: string;
  tenantId: string;
  fiscalCode?: string;
  membershipNumber?: string;
  phone?: string;
  privacyAcceptedAt: string;
  termsAcceptedAt: string;
}

/**
 * Interfaccia del contesto di autenticazione
 * esposta tramite il hook useAuth()
 */
interface AuthContextType {
  user: User | null;                    // Utente Supabase autenticato
  session: Session | null;              // Sessione corrente
  roles: AppRole[];                     // Tutti i ruoli dell'utente
  highestRole: AppRole | null;          // Ruolo più alto (più privilegi)
  simulatedRole: AppRole | null;        // Ruolo simulato per testing
  effectiveRole: AppRole | null;        // Ruolo effettivo (simulato o reale)
  loading: boolean;                     // True durante il caricamento iniziale
  signUp: (email: string, password: string, data: SignUpData, captchaToken: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;           // Verifica ruolo esatto
  hasRoleLevel: (minRole: AppRole) => boolean;   // Verifica livello minimo
  setSimulatedRole: (role: AppRole | null) => void;
  getAvailableSimulationRoles: () => AppRole[];  // Ruoli simulabili
}

/** Context React per condividere lo stato di auth nell'app */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Ottiene il livello numerico di un ruolo (0 = più alto).
 * Usato per confronti gerarchici.
 */
function getRoleLevel(role: AppRole): number {
  return ROLE_HIERARCHY.indexOf(role);
}

/**
 * Provider che avvolge l'app e fornisce il contesto di autenticazione.
 * Gestisce:
 * - Ascolto cambiamenti stato auth (login/logout)
 * - Caricamento ruoli utente dal database
 * - Logica di simulazione ruoli per testing
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  // Traccia se i ruoli dell'utente corrente sono stati caricati dal DB.
  // Senza questo flag, `loading` diventa false non appena la sessione è
  // nota, ma `roles` resta `[]` finché `fetchRoles` (differito con
  // setTimeout(0)) non risponde. In quella finestra `hasRoleLevel`
  // restituisce erroneamente false, causando redirect indesiderati
  // (es. Admin → /dashboard quando si clicca "Gestione").
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [simulatedRole, setSimulatedRole] = useState<AppRole | null>(null);

  /**
   * Carica i ruoli dell'utente dalla tabella user_roles.
   * Chiamato dopo login o al refresh della pagina.
   */
  const fetchRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("[useAuth] Error fetching roles:", error);
        return;
      }

      if (data && data.length > 0) {
        setRoles(data.map((r) => r.role as AppRole));
      } else {
        console.warn("[useAuth] No roles found for user:", userId);
        setRoles([]);
      }
    } catch (err) {
      console.error("[useAuth] Unexpected error fetching roles:", err);
    } finally {
      // Marca i ruoli come caricati (anche in caso di errore o lista vuota)
      // per sbloccare le guard di routing che attendono `loading=false`.
      setRolesLoaded(true);
    }
  };

  /**
   * Effect per gestire lo stato di autenticazione.
   * Ascolta i cambiamenti di sessione e carica i ruoli quando necessario.
   * Gestisce anche la logica "Ricordami" per sessioni temporanee.
   */
  useEffect(() => {
    // Controlla se la sessione era marcata come temporanea (non ricordami)
    // Se sì, effettua logout all'apertura della pagina (nuova sessione browser)
    const isSessionOnly = sessionStorage.getItem("lni_session_only");
    if (isSessionOnly === "true") {
      // Rimuovi il flag e verifica se siamo in una nuova sessione browser
      // Il flag in sessionStorage persiste durante la sessione corrente
      // ma scompare alla chiusura del browser
    }
    
    // Configura listener PRIMA di controllare la sessione esistente
    // per evitare race condition
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Gestisce eventi di errore token
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn("[useAuth] Token refresh failed, clearing session");
          setSession(null);
          setUser(null);
          setRoles([]);
          setRolesLoaded(true);
          setSimulatedRole(null);
          setLoading(false);
          return;
        }
        
        if (event === 'SIGNED_OUT') {
          console.info("[useAuth] User signed out");
          setSession(null);
          setUser(null);
          setRoles([]);
          setRolesLoaded(true);
          setSimulatedRole(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Nuovo utente: i ruoli vanno ricaricati, quindi resettiamo il flag
          // per evitare che il chiamante consideri ancora "caricati" i ruoli
          // dell'utente precedente.
          setRolesLoaded(false);
          // Differisce il caricamento ruoli per evitare deadlock
          setTimeout(() => {
            fetchRoles(session.user.id);
          }, 0);
        } else {
          // Pulisce lo stato al logout
          setRoles([]);
          setRolesLoaded(true);
          setSimulatedRole(null);
        }
        setLoading(false);
      }
    );

    // POI controlla se esiste già una sessione
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error("[useAuth] Error getting session:", error);
          // Non crashare, semplicemente considera l'utente non loggato
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setRolesLoaded(false);
          fetchRoles(session.user.id);
        } else {
          setRolesLoaded(true);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("[useAuth] Failed to get session:", error);
        setSession(null);
        setUser(null);
        setLoading(false);
      });

    // Cleanup: rimuove il listener alla unmount
    return () => subscription.unsubscribe();
  }, []);

  /** Registra un nuovo utente e salva i dati essenziali nei metadati. */
  const signUp = useCallback(async (email: string, password: string, data: SignUpData, captchaToken: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        // Supabase inoltra questo token al provider CAPTCHA configurato e rifiuta la signup se non è valido.
        captchaToken,
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
          tenant_id: data.tenantId,
          fiscal_code: data.fiscalCode || null,
          membership_number: data.membershipNumber || null,
          phone: data.phone || null,
          privacy_accepted_at: data.privacyAcceptedAt,
          terms_accepted_at: data.termsAcceptedAt,
        },
      },
    });

    return { error: error as Error | null };
  }, []);

  /**
   * Effettua il login con email e password.
   */
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  }, []);

  /**
   * Effettua il logout e pulisce lo stato.
   */
  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
    setSimulatedRole(null);
  }, []);

  /**
   * Calcola il ruolo più alto (con più privilegi) tra quelli dell'utente.
   * Il ruolo più alto è quello con l'indice minore nell'array ROLE_HIERARCHY.
   */
  const highestRole = roles.length > 0
    ? roles.reduce((highest, current) => 
        getRoleLevel(current) < getRoleLevel(highest) ? current : highest
      )
    : null;

  /**
   * Ruolo effettivo considerando l'eventuale simulazione.
   * Usato per controllare i permessi nell'UI.
   */
  const effectiveRole = simulatedRole ?? highestRole;

  /**
   * Verifica se l'utente ha esattamente un ruolo specifico.
   * Considera la simulazione se attiva.
   */
  const hasRole = useCallback((role: AppRole) => {
    if (simulatedRole) {
      return simulatedRole === role;
    }
    return roles.includes(role);
  }, [simulatedRole, roles]);

  /**
   * Verifica se l'utente ha almeno un certo livello di ruolo.
   * Esempio: hasRoleLevel('autore') ritorna true se l'utente è
   * autore, responsabile o amministratore.
   * Considera la simulazione se attiva.
   */
  const hasRoleLevel = useCallback((minRole: AppRole) => {
    const minLevel = getRoleLevel(minRole);
    
    if (simulatedRole) {
      return getRoleLevel(simulatedRole) <= minLevel;
    }
    
    // Basta che uno dei ruoli dell'utente sia al livello richiesto o superiore
    return roles.some(role => getRoleLevel(role) <= minLevel);
  }, [simulatedRole, roles]);

  /**
   * Ottiene i ruoli disponibili per la simulazione.
   * Un utente può simulare solo ruoli con livello >= al proprio ruolo più alto.
   * Esempio: un responsabile può simulare formatore, tutor, istruttore, ecc.
   */
  const getAvailableSimulationRoles = useCallback((): AppRole[] => {
    if (!highestRole) return [];
    
    const highestLevel = getRoleLevel(highestRole);
    // Filtra i ruoli con indice >= al livello più alto dell'utente
    return ROLE_HIERARCHY.filter((_, index) => index >= highestLevel);
  }, [highestRole]);

  const contextValue = useMemo(() => ({
    user,
    session,
    roles,
    highestRole,
    simulatedRole,
    effectiveRole,
    // Esponiamo come "loading" anche la finestra in cui la sessione è
    // nota ma i ruoli non sono ancora stati recuperati: in caso contrario
    // le guard tipo `hasRoleLevel("formatore")` vedrebbero `roles=[]` e
    // redirigerebbero erroneamente.
    loading: loading || (!!user && !rolesLoaded),
    signUp,
    signIn,
    signOut,
    hasRole,
    hasRoleLevel,
    setSimulatedRole,
    getAvailableSimulationRoles,
  }), [user, session, roles, highestRole, simulatedRole, effectiveRole, loading, rolesLoaded, signUp, signIn, signOut, hasRole, hasRoleLevel, setSimulatedRole, getAvailableSimulationRoles]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook per accedere al contesto di autenticazione.
 * Deve essere usato all'interno di un AuthProvider.
 * 
 * @throws Error se usato fuori da AuthProvider
 * @returns Il contesto con stato auth e funzioni
 * 
 * @example
 * const { user, hasRoleLevel, signOut } = useAuth();
 * if (hasRoleLevel('istruttore')) {
 *   // mostra funzionalità per istruttori
 * }
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
